/**
 * NotificationService - Centralized toast/notification system
 * 
 * Replaces all alert() calls with non-blocking toast notifications.
 * 
 * Usage:
 * import { notify, notifyError, notifySuccess, notifyWarning, notifyInfo } from '../services/notificationService';
 * 
 * // Simple notifications
 * notifySuccess('Quiz erfolgreich gespeichert!');
 * notifyError('Fehler beim Laden der Daten');
 * notifyWarning('Session wird in 5 Minuten beendet');
 * notifyInfo('Neue Frage hinzugefügt');
 * 
 * // With custom options
 * notify('Custom message', { type: 'success', duration: 5000, title: 'Erfolg!' });
 * 
 * // Confirm dialog replacement
 * const confirmed = await notifyConfirm('Möchtest du fortfahren?');
 * if (confirmed) { ... }
 */

import { Logger } from '../utils/logger';

const log = Logger.create('NotificationService');

// ============================================================================
// Notification Store (Simple pub/sub for React components)
// ============================================================================

let notificationId = 0;
const listeners = new Set();
let notifications = [];

/**
 * Subscribe to notification changes
 * @param {Function} callback - Called with notifications array when changed
 * @returns {Function} Unsubscribe function
 */
export function subscribeToNotifications(callback) {
  listeners.add(callback);
  callback(notifications); // Initial call with current state
  return () => listeners.delete(callback);
}

/**
 * Notify all listeners of state change
 */
function notifyListeners() {
  listeners.forEach(callback => callback([...notifications]));
}

// ============================================================================
// Core Notification Functions
// ============================================================================

/**
 * Notification types with their default styles
 */
export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

/**
 * Default durations per type (in ms)
 */
const DEFAULT_DURATIONS = {
  success: 3000,
  error: 5000,
  warning: 4000,
  info: 3500
};

/**
 * Add a notification
 * @param {string} message - The notification message
 * @param {Object} options - Notification options
 * @param {string} options.type - 'success' | 'error' | 'warning' | 'info'
 * @param {number} options.duration - Duration in ms (0 = permanent until dismissed)
 * @param {string} options.title - Optional title
 * @param {boolean} options.dismissible - Whether user can dismiss (default: true)
 * @param {Function} options.action - Optional action button callback
 * @param {string} options.actionLabel - Label for action button
 * @returns {number} Notification ID
 */
export function notify(message, options = {}) {
  const {
    type = NOTIFICATION_TYPES.INFO,
    duration = DEFAULT_DURATIONS[type] || 3000,
    title,
    dismissible = true,
    action,
    actionLabel
  } = options;

  const id = ++notificationId;
  
  const notification = {
    id,
    message,
    type,
    title,
    dismissible,
    action,
    actionLabel,
    createdAt: Date.now()
  };

  notifications = [...notifications, notification];
  notifyListeners();
  
  log.debug(`Notification added: [${type}] ${message}`);

  // Auto-dismiss after duration (unless duration is 0)
  if (duration > 0) {
    setTimeout(() => {
      dismissNotification(id);
    }, duration);
  }

  return id;
}

/**
 * Dismiss a notification by ID
 * @param {number} id - Notification ID to dismiss
 */
export function dismissNotification(id) {
  const notification = notifications.find(n => n.id === id);
  if (notification) {
    notifications = notifications.filter(n => n.id !== id);
    notifyListeners();
    log.debug(`Notification dismissed: ${id}`);
  }
}

/**
 * Clear all notifications
 */
export function clearAllNotifications() {
  notifications = [];
  notifyListeners();
  log.debug('All notifications cleared');
}

// ============================================================================
// Convenience Functions (Most common use cases)
// ============================================================================

/**
 * Show success notification
 * @param {string} message 
 * @param {Object} options
 */
export function notifySuccess(message, options = {}) {
  return notify(message, { ...options, type: NOTIFICATION_TYPES.SUCCESS });
}

/**
 * Show error notification
 * @param {string} message 
 * @param {Object} options
 */
export function notifyError(message, options = {}) {
  // Errors stay longer by default
  return notify(message, { duration: 5000, ...options, type: NOTIFICATION_TYPES.ERROR });
}

/**
 * Show warning notification
 * @param {string} message 
 * @param {Object} options
 */
export function notifyWarning(message, options = {}) {
  return notify(message, { ...options, type: NOTIFICATION_TYPES.WARNING });
}

/**
 * Show info notification
 * @param {string} message 
 * @param {Object} options
 */
export function notifyInfo(message, options = {}) {
  return notify(message, { ...options, type: NOTIFICATION_TYPES.INFO });
}

// ============================================================================
// Confirm Dialog Replacement
// ============================================================================

let confirmResolve = null;
let confirmNotificationId = null;

/**
 * Subscribe to confirm dialog state
 * Used by ConfirmDialog component
 */
export const confirmDialogState = {
  listeners: new Set(),
  current: null,
  subscribe(callback) {
    this.listeners.add(callback);
    callback(this.current);
    return () => this.listeners.delete(callback);
  },
  set(state) {
    this.current = state;
    this.listeners.forEach(cb => cb(state));
  }
};

/**
 * Show a confirm dialog (replacement for window.confirm())
 * @param {string} message - Confirmation message
 * @param {Object} options - Dialog options
 * @param {string} options.title - Dialog title
 * @param {string} options.confirmLabel - Confirm button label (default: 'Bestätigen')
 * @param {string} options.cancelLabel - Cancel button label (default: 'Abbrechen')
 * @param {string} options.variant - 'danger' | 'warning' | 'default'
 * @returns {Promise<boolean>} True if confirmed, false if cancelled
 */
export function notifyConfirm(message, options = {}) {
  const {
    title = 'Bestätigung',
    confirmLabel = 'Bestätigen',
    cancelLabel = 'Abbrechen',
    variant = 'default'
  } = options;

  return new Promise((resolve) => {
    confirmResolve = resolve;
    
    confirmDialogState.set({
      message,
      title,
      confirmLabel,
      cancelLabel,
      variant,
      isOpen: true
    });
    
    log.debug(`Confirm dialog opened: ${message}`);
  });
}

/**
 * Resolve the current confirm dialog
 * Used by ConfirmDialog component
 */
export function resolveConfirm(confirmed) {
  if (confirmResolve) {
    confirmResolve(confirmed);
    confirmResolve = null;
  }
  confirmDialogState.set(null);
  log.debug(`Confirm dialog resolved: ${confirmed}`);
}

// ============================================================================
// Legacy Alert Replacement
// ============================================================================

/**
 * Drop-in replacement for alert() - shows as a toast instead
 * @param {string} message 
 */
export function showAlert(message) {
  // Determine type from message content
  if (message.toLowerCase().includes('fehler') || message.toLowerCase().includes('error')) {
    return notifyError(message);
  } else if (message.toLowerCase().includes('warnung') || message.toLowerCase().includes('warning') || message.includes('⚠️')) {
    return notifyWarning(message);
  } else if (message.toLowerCase().includes('erfolg') || message.toLowerCase().includes('gespeichert') || message.includes('✓') || message.includes('✅')) {
    return notifySuccess(message);
  } else {
    return notifyInfo(message);
  }
}

// ============================================================================
// Export Default
// ============================================================================

export default {
  notify,
  notifySuccess,
  notifyError,
  notifyWarning,
  notifyInfo,
  notifyConfirm,
  showAlert,
  dismissNotification,
  clearAllNotifications,
  subscribeToNotifications,
  confirmDialogState,
  resolveConfirm,
  NOTIFICATION_TYPES
};
